import { useState, useEffect, useRef } from "react";

const TABS = [
  { id: "chat", label: "AI Agent", icon: "🤖" },
  { id: "draft", label: "Draft Room", icon: "📋" },
  { id: "lineup", label: "Lineup", icon: "⚡" },
  { id: "trade", label: "Trade Analyzer", icon: "🔄" },
  { id: "waiver", label: "Waiver Wire", icon: "📡" },
  { id: "news", label: "Intel Feed", icon: "📰" },
];

const MOCK_ROSTER = [
  { slot: "QB", name: "Lamar Jackson", team: "BAL", pos: "QB", pts: 28.4, status: "healthy", trend: "up", proj: 26.1 },
  { slot: "RB1", name: "Christian McCaffrey", team: "SF", pos: "RB", pts: 31.2, status: "healthy", trend: "up", proj: 24.8 },
  { slot: "RB2", name: "Bijan Robinson", team: "ATL", pos: "RB", pts: 18.6, status: "questionable", trend: "neutral", proj: 16.2 },
  { slot: "WR1", name: "Tyreek Hill", team: "MIA", pos: "WR", pts: 22.1, status: "healthy", trend: "up", proj: 20.5 },
  { slot: "WR2", name: "Amon-Ra St. Brown", team: "DET", pos: "WR", pts: 19.8, status: "healthy", trend: "up", proj: 18.9 },
  { slot: "TE", name: "Sam LaPorta", team: "DET", pos: "TE", pts: 14.2, status: "healthy", trend: "up", proj: 13.8 },
  { slot: "FLEX", name: "Puka Nacua", team: "LAR", pos: "WR", pts: 17.4, status: "healthy", trend: "neutral", proj: 15.2 },
  { slot: "K", name: "Justin Tucker", team: "BAL", pos: "K", pts: 10.0, status: "healthy", trend: "neutral", proj: 9.5 },
  { slot: "DST", name: "San Francisco 49ers", team: "SF", pos: "DST", pts: 11.0, status: "healthy", trend: "up", proj: 10.2 },
  { slot: "BE1", name: "Gus Edwards", team: "LAC", pos: "RB", pts: 8.4, status: "healthy", trend: "neutral", proj: 9.1 },
  { slot: "BE2", name: "Rashee Rice", team: "KC", pos: "WR", pts: 15.6, status: "healthy", trend: "up", proj: 14.4 },
  { slot: "BE3", name: "Jonnu Smith", team: "MIA", pos: "TE", pts: 7.2, status: "healthy", trend: "neutral", proj: 8.0 },
  { slot: "BE4", name: "Jaylen Warren", team: "PIT", pos: "RB", pts: 10.1, status: "out", trend: "down", proj: 0 },
  { slot: "BE5", name: "Odell Beckham Jr.", team: "MIA", pos: "WR", pts: 9.3, status: "questionable", trend: "down", proj: 6.1 },
  { slot: "BE6", name: "Dallas Cowboys", team: "DAL", pos: "DST", pts: 6.0, status: "healthy", trend: "neutral", proj: 7.5 },
];

const MOCK_NEWS = [
  { type: "injury", player: "Bijan Robinson", team: "ATL", headline: "Robinson limited in practice Wednesday with ankle", detail: "Listed as questionable for Week 9. Avery Williams could see increased work.", time: "2h ago", severity: "medium" },
  { type: "move", player: "Jaylen Warren", team: "PIT", headline: "Warren ruled out for Sunday vs. Browns", detail: "Najee Harris expected to handle full backfield duties. High-volume game likely.", time: "3h ago", severity: "high" },
  { type: "presser", player: "Kyle Shanahan", team: "SF", headline: "Shanahan confirms McCaffrey 'full go' in practice", detail: "No limitations, no injury designation. Elite matchup vs. JAX run D (30th in DVOA).", time: "4h ago", severity: "low" },
  { type: "camp", player: "Rashee Rice", team: "KC", headline: "Rice drawing rave reviews — target hog emerging", detail: "Mahomes looking his way early and often. WR1 upside in PPR formats.", time: "5h ago", severity: "low" },
  { type: "matchup", player: "Tyreek Hill", team: "MIA", headline: "MIA vs. NE: Hill faces shadow CB coverage", detail: "Jalen Mills likely to follow Hill. Historically 15% reduction in targets when shadowed.", time: "6h ago", severity: "medium" },
  { type: "waiver", player: "Gabe Davis", team: "JAX", headline: "Davis 40% owned — WR2/3 upside vs. SF secondary", detail: "With Ridley banged up, Davis is the alpha. 8 targets last week.", time: "7h ago", severity: "low" },
];

const MOCK_WAIVERS = [
  { name: "Jaleel McLaughlin", team: "DEN", pos: "RB", owned: 18, priority: 98, reason: "Javonte Williams (knee) IR'd — McLaughlin is RB1 now", pts: 0, proj: 14.2, add: "YES" },
  { name: "Gabe Davis", team: "JAX", pos: "WR", owned: 41, priority: 87, reason: "Ridley questionable, Davis alpha WR vs SF (30th pass D)", pts: 9.8, proj: 13.5, add: "YES" },
  { name: "Jerome Ford", team: "CLE", pos: "RB", owned: 54, priority: 74, reason: "Nick Chubb out — Ford locked in as lead back through Week 14", pts: 12.1, proj: 12.8, add: "CONSIDER" },
  { name: "Elijah Higgins", team: "CIN", pos: "TE", owned: 6, priority: 61, reason: "Emerging as Burrow's 3rd-down target. TE streamers have worse options", pts: 4.2, proj: 9.1, add: "CONSIDER" },
  { name: "Seattle Seahawks", team: "SEA", pos: "DST", owned: 22, priority: 55, reason: "Week 9 matchup vs CAR (worst O-line in NFL). Sack upside", pts: 7.0, proj: 11.4, add: "CONSIDER" },
];

const DRAFT_POOL = [
  { rank: 1, name: "Christian McCaffrey", pos: "RB", team: "SF", adp: 1.2, proj: 340, tier: 1 },
  { rank: 2, name: "CeeDee Lamb", pos: "WR", team: "DAL", adp: 2.1, proj: 310, tier: 1 },
  { rank: 3, name: "Tyreek Hill", pos: "WR", team: "MIA", adp: 3.4, proj: 298, tier: 1 },
  { rank: 4, name: "Ja'Marr Chase", pos: "WR", team: "CIN", adp: 4.0, proj: 290, tier: 1 },
  { rank: 5, name: "Austin Ekeler", pos: "RB", team: "LAC", adp: 4.8, proj: 275, tier: 1 },
  { rank: 6, name: "Bijan Robinson", pos: "RB", team: "ATL", adp: 5.6, proj: 268, tier: 1 },
  { rank: 7, name: "Justin Jefferson", pos: "WR", team: "MIN", adp: 6.2, proj: 261, tier: 1 },
  { rank: 8, name: "Davante Adams", pos: "WR", team: "LV", adp: 7.1, proj: 254, tier: 2 },
  { rank: 9, name: "Tony Pollard", pos: "RB", team: "DAL", adp: 7.8, proj: 248, tier: 2 },
  { rank: 10, name: "Stefon Diggs", pos: "WR", team: "BUF", adp: 8.3, proj: 241, tier: 2 },
  { rank: 11, name: "Travis Kelce", pos: "TE", team: "KC", adp: 8.9, proj: 235, tier: 2 },
  { rank: 12, name: "Lamar Jackson", pos: "QB", team: "BAL", adp: 9.4, proj: 420, tier: 2 },
  { rank: 13, name: "A.J. Brown", pos: "WR", team: "PHI", adp: 9.8, proj: 228, tier: 2 },
  { rank: 14, name: "Josh Allen", pos: "QB", team: "BUF", adp: 10.2, proj: 418, tier: 2 },
  { rank: 15, name: "Breece Hall", pos: "RB", team: "NYJ", adp: 10.7, proj: 222, tier: 2 },
];

const API_KEY = "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";
const PASSWORD = "Bears2354$";

const statusColor = (s) => s === "healthy" ? "#22c55e" : s === "questionable" ? "#f59e0b" : "#ef4444";
const statusLabel = (s) => s === "healthy" ? "✓" : s === "questionable" ? "?" : "OUT";
const trendIcon = (t) => t === "up" ? "↑" : t === "down" ? "↓" : "→";
const trendColor = (t) => t === "up" ? "#22c55e" : t === "down" ? "#ef4444" : "#94a3b8";
const posColor = (p) => ({ QB: "#f97316", RB: "#22c55e", WR: "#3b82f6", TE: "#a855f7", K: "#94a3b8", DST: "#64748b", FLEX: "#06b6d4" }[p] || "#94a3b8");

const API_HEADERS = {
  "Content-Type": "application/json",
  "x-api-key": API_KEY,
  "anthropic-version": "2023-06-01",
  "anthropic-dangerous-direct-browser-access": "true"
};

export default function App() {
  const [unlocked, setUnlocked] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [activeTab, setActiveTab] = useState("chat");
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Welcome to **BlitzIQ** — your AI-powered fantasy football command center.\n\nI'm trained on daily NFL news, injury reports, depth charts, and matchup data. Here's what I can help you with right now:\n\n• **Draft prep** — Where to pick, best available by position, positional scarcity\n• **Weekly lineups** — Start/sit decisions with matchup grades\n• **Trade analysis** — Win/lose breakdown + counter-offer suggestions\n• **Waiver wire** — Priority pickups ranked by impact\n• **Injury news** — Daily updates from beat reporters + practice reports\n\nWhat do you need today?" }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [draftPick, setDraftPick] = useState(1);
  const [draftedTeam, setDraftedTeam] = useState([]);
  const [tradeGive, setTradeGive] = useState("");
  const [tradeGet, setTradeGet] = useState("");
  const [tradeResult, setTradeResult] = useState(null);
  const [tradeLoading, setTradeLoading] = useState(false);
  const [draftLoading, setDraftLoading] = useState(false);
  const [draftRec, setDraftRec] = useState(null);
  const chatEndRef = useRef(null);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  if (!unlocked) {
    return (
      <div style={{ fontFamily: "'DM Mono','Courier New',monospace", background: "#050810", minHeight: "100vh", color: "#e2e8f0", display: "flex", alignItems: "center", justifyContent: "center", position: "relative", overflow: "hidden" }}>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&display=swap');
          *{box-sizing:border-box;margin:0;padding:0}
          @keyframes pulse{0%,100%{opacity:.3}50%{opacity:1}}
          @keyframes scan{0%{transform:translateY(-200px)}100%{transform:translateY(800px)}}
          @keyframes flicker{0%,100%{opacity:1}93%{opacity:.7}95%{opacity:1}}
        `}</style>

        {/* Grid */}
        <div style={{ position:"absolute",inset:0,backgroundImage:"linear-gradient(#f9731606 1px,transparent 1px),linear-gradient(90deg,#f9731606 1px,transparent 1px)",backgroundSize:"50px 50px" }} />
        {/* Glow */}
        <div style={{ position:"absolute",top:0,left:"50%",transform:"translateX(-50%)",width:900,height:350,background:"radial-gradient(ellipse,#f9731616 0%,transparent 65%)",pointerEvents:"none" }} />
        {/* Scan line */}
        <div style={{ position:"absolute",left:0,right:0,height:1,background:"linear-gradient(90deg,transparent,#f9731655,transparent)",animation:"scan 6s linear infinite" }} />
        {/* Corner brackets */}
        <div style={{ position:"absolute",top:16,left:16,width:36,height:36,borderTop:"2px solid #f97316",borderLeft:"2px solid #f97316" }} />
        <div style={{ position:"absolute",top:16,right:16,width:36,height:36,borderTop:"2px solid #f97316",borderRight:"2px solid #f97316" }} />
        <div style={{ position:"absolute",bottom:16,left:16,width:36,height:36,borderBottom:"2px solid #f97316",borderLeft:"2px solid #f97316" }} />
        <div style={{ position:"absolute",bottom:16,right:16,width:36,height:36,borderBottom:"2px solid #f97316",borderRight:"2px solid #f97316" }} />

        <div style={{ textAlign:"center",zIndex:10,padding:28 }}>
          {/* SpeedFlex Helmet SVG */}
          <svg width="140" height="150" viewBox="0 0 140 160" style={{ display:"block",margin:"0 auto 18px" }}>
            <defs>
              <radialGradient id="shell" cx="50%" cy="30%">
                <stop offset="0%" stopColor="#1e2d45"/>
                <stop offset="100%" stopColor="#080c14"/>
              </radialGradient>
              <radialGradient id="flex" cx="50%" cy="50%">
                <stop offset="0%" stopColor="#f9731622"/>
                <stop offset="100%" stopColor="#0d1320"/>
              </radialGradient>
            </defs>
            <path d="M70 6 C38 6 14 28 14 56 C14 74 18 86 24 94 C24 94 22 100 22 106 C22 110 26 112 30 110 L38 106 C48 110 58 112 70 112 C82 112 92 110 102 106 L110 110 C114 112 118 110 118 106 C118 100 116 94 116 94 C122 86 126 74 126 56 C126 28 102 6 70 6Z" fill="url(#shell)" stroke="#f97316" strokeWidth="1.8"/>
            <rect x="52" y="12" width="36" height="22" rx="5" fill="url(#flex)" stroke="#f97316" strokeWidth="1.2"/>
            <line x1="58" y1="16" x2="82" y2="16" stroke="#f97316" strokeWidth="0.6" opacity="0.5"/>
            <line x1="56" y1="20" x2="84" y2="20" stroke="#f97316" strokeWidth="0.6" opacity="0.5"/>
            <line x1="56" y1="24" x2="84" y2="24" stroke="#f97316" strokeWidth="0.6" opacity="0.5"/>
            <line x1="58" y1="28" x2="82" y2="28" stroke="#f97316" strokeWidth="0.6" opacity="0.5"/>
            <line x1="70" y1="6" x2="70" y2="112" stroke="#f97316" strokeWidth="2.5"/>
            <path d="M14 72 C8 72 6 80 8 88 C10 96 18 102 26 104" stroke="#f97316" strokeWidth="2" fill="none" strokeLinecap="round"/>
            <path d="M126 72 C132 72 134 80 132 88 C130 96 122 102 114 104" stroke="#f97316" strokeWidth="2" fill="none" strokeLinecap="round"/>
            <path d="M8 80 C6 84 6 90 10 96" stroke="#f97316" strokeWidth="3" strokeLinecap="round" fill="none" opacity="0.7"/>
            <path d="M132 80 C134 84 134 90 130 96" stroke="#f97316" strokeWidth="3" strokeLinecap="round" fill="none" opacity="0.7"/>
            <circle cx="16" cy="68" r="5" fill="#050810" stroke="#f97316" strokeWidth="1.2"/>
            <circle cx="124" cy="68" r="5" fill="#050810" stroke="#f97316" strokeWidth="1.2"/>
            <path d="M36 54 C36 44 50 36 70 36 C90 36 104 44 104 54 L104 96 C104 104 90 112 70 112 C50 112 36 104 36 96 Z" fill="#050810"/>
            <path d="M36 52 C36 40 50 32 70 32 C90 32 104 40 104 52" stroke="#f97316" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
            <line x1="36" y1="52" x2="36" y2="100" stroke="#f97316" strokeWidth="2.5"/>
            <line x1="104" y1="52" x2="104" y2="100" stroke="#f97316" strokeWidth="2.5"/>
            <path d="M36 100 C36 108 50 114 70 114 C90 114 104 108 104 100" stroke="#f97316" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
            <line x1="36" y1="64" x2="104" y2="64" stroke="#f97316" strokeWidth="2.2"/>
            <line x1="36" y1="80" x2="104" y2="80" stroke="#f97316" strokeWidth="2.2"/>
            <path d="M52 32 C50 48 50 68 52 100" stroke="#f97316" strokeWidth="2" strokeLinecap="round" fill="none"/>
            <line x1="70" y1="32" x2="70" y2="114" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
            <path d="M88 32 C90 48 90 68 88 100" stroke="#f97316" strokeWidth="2" strokeLinecap="round" fill="none"/>
            <path d="M80 10 L96 10 L96 24 L108 24" stroke="#f97316" strokeWidth="0.8" strokeDasharray="3 3" opacity="0.45"/>
            <path d="M60 10 L44 10 L44 24 L32 24" stroke="#f97316" strokeWidth="0.8" strokeDasharray="3 3" opacity="0.35"/>
            <circle cx="96" cy="24" r="2" fill="#f97316" opacity="0.7"/>
            <circle cx="44" cy="24" r="2" fill="#f97316" opacity="0.5"/>
            <path d="M28 30 C38 16 54 8 68 7" stroke="white" strokeWidth="1.5" strokeLinecap="round" opacity="0.1"/>
            <path d="M38 106 C42 118 56 124 70 124 C84 124 98 118 102 106" stroke="#f97316" strokeWidth="1.5" strokeDasharray="4 3" fill="none" opacity="0.6"/>
          </svg>

          <div style={{ fontFamily:"'Bebas Neue',sans-serif",fontSize:54,letterSpacing:6,color:"#f97316",lineHeight:1,marginBottom:4 }}>BLITZ<span style={{ color:"#e2e8f0" }}>IQ</span></div>
          <div style={{ fontSize:9,color:"#4a5568",letterSpacing:4,textTransform:"uppercase",marginBottom:32 }}>AI Fantasy Football Command Center</div>

          <div style={{ display:"flex",justifyContent:"center",gap:28,marginBottom:32 }}>
            {[["SYSTEMS ONLINE",1.5],["DATA LIVE",2],["AI READY",2.5]].map(([label,dur]) => (
              <div key={label} style={{ display:"flex",alignItems:"center",gap:6 }}>
                <div style={{ width:5,height:5,borderRadius:"50%",background:"#22c55e",boxShadow:"0 0 8px #22c55e",animation:`pulse ${dur}s infinite` }} />
                <span style={{ fontSize:9,color:"#4a5568",letterSpacing:1 }}>{label}</span>
              </div>
            ))}
          </div>

          <div style={{ background:"#0d1320",border:"1px solid #1a2235",padding:"28px 36px",maxWidth:360,margin:"0 auto",position:"relative" }}>
            <div style={{ position:"absolute",top:0,left:0,right:0,height:2,background:"#f97316" }} />
            <div style={{ fontSize:9,color:"#4a5568",letterSpacing:3,textTransform:"uppercase",marginBottom:18 }}>⬡ Restricted Access</div>
            <input
              type="password"
              value={passwordInput}
              onChange={e => setPasswordInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && passwordInput === PASSWORD && setUnlocked(true)}
              placeholder="Enter password"
              style={{ background:"#080c14",border:"1px solid #1a2235",color:"#e2e8f0",fontFamily:"inherit",fontSize:13,padding:"12px 16px",outline:"none",width:"100%",marginBottom:12,letterSpacing:2,boxSizing:"border-box" }}
            />
            <button
              onClick={() => passwordInput === PASSWORD && setUnlocked(true)}
              style={{ background:"#f97316",color:"#000",border:"none",padding:12,fontFamily:"inherit",fontSize:10,fontWeight:700,letterSpacing:3,cursor:"pointer",width:"100%",textTransform:"uppercase" }}
            >
              Access BlitzIQ →
            </button>
            {passwordInput && passwordInput !== PASSWORD && (
              <div style={{ fontSize:10,color:"#ef4444",marginTop:12,letterSpacing:2 }}>✗ Invalid credentials</div>
            )}
            <div style={{ fontSize:9,color:"#2d3748",letterSpacing:2,marginTop:16,textTransform:"uppercase" }}>Authorized Personnel Only</div>
          </div>
        </div>
      </div>
    );
  }

  const systemPrompt = `You are BlitzIQ, an elite fantasy football AI agent. Every question is about FANTASY FOOTBALL — never ask for clarification, always assume fantasy context.

RESPONSE STYLE — CRITICAL:
- Keep responses SHORT and punchy
- Use bullet points and line breaks for readability
- Bold key player names and decisions using **name**
- Lead with the recommendation immediately
- Max 3-4 sentences of reasoning
- No fluff, no filler, no over-explaining
- Format like a text from your smartest fantasy friend

Example good response:
"**McCaffrey** at #1. No debate.
• Elite RB1, PPR monster, goal line work
• Biggest gap in fantasy is RB tier 1 vs 2
• WR depth is fine in rounds 2-3
→ Lock it in."

You have deep knowledge of NFL rosters, injuries, depth charts, fantasy scoring (PPR/half-PPR/standard), draft strategy, lineup optimization, trade values, waiver wire, and matchup data.

Always assume fantasy football context. Give direct confident advice. For trades give WIN/LOSS/EVEN verdict. For drafts give an immediate pick.

Current context: NFL 2025 season, preseason. Season starts September 2025.`;

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userMsg = { role: "user", content: input };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput("");
    setLoading(true);
    try {
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: API_HEADERS,
        body: JSON.stringify({
          model: "claude-haiku-4-5-20251001",
          max_tokens: 1000,
          system: systemPrompt,
          messages: newMessages.map(m => ({ role: m.role, content: m.content })),
        })
      });
      const data = await res.json();
      const reply = data.content?.[0]?.text || "I couldn't process that. Try again.";
      setMessages(prev => [...prev, { role: "assistant", content: reply }]);
    } catch {
      setMessages(prev => [...prev, { role: "assistant", content: "Connection error. Please try again." }]);
    }
    setLoading(false);
  };

  const getDraftRec = async () => {
    setDraftLoading(true);
    setDraftRec(null);
    const drafted = draftedTeam.map(p => p.name).join(", ");
    const available = DRAFT_POOL.filter(p => !draftedTeam.find(d => d.name === p.name)).slice(0, 8).map(p => `${p.name} (${p.pos}, ${p.team}, ADP ${p.adp})`).join(", ");
    try {
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: API_HEADERS,
        body: JSON.stringify({
          model: "claude-haiku-4-5-20251001",
          max_tokens: 1000,
          system: systemPrompt + "\n\nRespond in JSON only: { recommendation: string, position: string, reasoning: string, risk: 'low'|'medium'|'high', alternates: [{ name: string, reasoning: string }] }",
          messages: [{ role: "user", content: `12-team PPR snake draft. My pick position: ${draftPick}. My picks so far: ${drafted || "None"}. Available: ${available}. Who should I draft?` }]
        })
      });
      const data = await res.json();
      const text = data.content?.[0]?.text || "{}";
      setDraftRec(JSON.parse(text.replace(/```json|```/g, "").trim()));
    } catch {
      setDraftRec({ recommendation: "Error", reasoning: "Could not get recommendation.", risk: "medium", alternates: [] });
    }
    setDraftLoading(false);
  };

  const analyzeTrade = async () => {
    if (!tradeGive.trim() || !tradeGet.trim()) return;
    setTradeLoading(true);
    setTradeResult(null);
    try {
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: API_HEADERS,
        body: JSON.stringify({
          model: "claude-haiku-4-5-20251001",
          max_tokens: 1000,
          system: systemPrompt + "\n\nRespond in JSON only: { verdict: 'ACCEPT'|'DECLINE'|'COUNTER', score: number, reasoning: string, pros: string[], cons: string[], counter: string | null, analysis: string }",
          messages: [{ role: "user", content: `Trade analysis: I GIVE: ${tradeGive} | I GET: ${tradeGet}. Is this good for me?` }]
        })
      });
      const data = await res.json();
      const text = data.content?.[0]?.text || "{}";
      setTradeResult(JSON.parse(text.replace(/```json|```/g, "").trim()));
    } catch {
      setTradeResult({ verdict: "ERROR", reasoning: "Could not analyze." });
    }
    setTradeLoading(false);
  };

  const renderMessage = (content) => content.split('\n').map((line, i) => {
    const bold = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    const bullet = line.startsWith('•') ? `<span style="color:#f97316">•</span>${line.slice(1)}` : bold;
    return <div key={i} dangerouslySetInnerHTML={{ __html: bullet }} style={{ marginBottom: line === '' ? 8 : 2 }} />;
  });

  return (
    <div style={{ fontFamily: "'DM Mono','Courier New',monospace", background: "#080c14", minHeight: "100vh", color: "#e2e8f0", display: "flex", flexDirection: "column" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@300;400;500&family=Bebas+Neue&display=swap');
        *{box-sizing:border-box;margin:0;padding:0}
        ::-webkit-scrollbar{width:4px}::-webkit-scrollbar-track{background:#0d1320}::-webkit-scrollbar-thumb{background:#f97316;border-radius:2px}
        .tab-btn{background:none;border:none;cursor:pointer;padding:10px 16px;font-family:inherit;font-size:11px;letter-spacing:1px;text-transform:uppercase;transition:all 0.2s;white-space:nowrap}
        .tab-btn:hover{color:#f97316}.tab-btn.active{color:#f97316;border-bottom:2px solid #f97316}
        .send-btn{background:#f97316;color:#000;border:none;padding:10px 20px;font-family:inherit;font-size:12px;font-weight:500;letter-spacing:1px;cursor:pointer;transition:all 0.15s;text-transform:uppercase}
        .send-btn:hover{background:#fb923c}.send-btn:disabled{background:#374151;color:#6b7280;cursor:not-allowed}
        .input-field{background:#111827;border:1px solid #1f2937;color:#e2e8f0;font-family:inherit;font-size:13px;padding:10px 14px;outline:none;width:100%}
        .input-field:focus{border-color:#f97316}
        .card{background:#0d1320;border:1px solid #1a2235;padding:16px}
        .quick-btn{background:#111827;border:1px solid #1f2937;color:#94a3b8;padding:6px 12px;font-family:inherit;font-size:10px;cursor:pointer;letter-spacing:0.5px;transition:all 0.15s;text-align:left}
        .quick-btn:hover{border-color:#f97316;color:#f97316}
        .draft-pick-btn{background:#0d1320;border:1px solid #f97316;color:#f97316;padding:8px 14px;font-family:inherit;font-size:12px;cursor:pointer;letter-spacing:1px;transition:all 0.15s}
        .draft-pick-btn:hover{background:#f97316;color:#000}
      `}</style>

      <div style={{ background: "#080c14", borderBottom: "1px solid #1a2235", padding: "14px 24px", display: "flex", alignItems: "center", gap: 16 }}>
        <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 28, letterSpacing: 3, color: "#f97316" }}>BLITZ<span style={{ color: "#e2e8f0" }}>IQ</span></div>
        <div style={{ width: 1, height: 24, background: "#1a2235" }} />
        <div style={{ fontSize: 10, color: "#64748b", letterSpacing: 1, textTransform: "uppercase" }}>AI Fantasy Football Command Center</div>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#22c55e", boxShadow: "0 0 6px #22c55e" }} />
          <span style={{ fontSize: 10, color: "#22c55e", letterSpacing: 1 }}>LIVE</span>
        </div>
      </div>

      <div style={{ background: "#080c14", borderBottom: "1px solid #1a2235", display: "flex", overflowX: "auto", padding: "0 24px" }}>
        {TABS.map(t => (
          <button key={t.id} className={`tab-btn ${activeTab === t.id ? "active" : ""}`} style={{ color: activeTab === t.id ? "#f97316" : "#64748b" }} onClick={() => setActiveTab(t.id)}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>

        {activeTab === "chat" && (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
              {messages.map((m, i) => (
                <div key={i} style={{ display: "flex", gap: 12, justifyContent: m.role === "user" ? "flex-end" : "flex-start" }}>
                  {m.role === "assistant" && <div style={{ width: 28, height: 28, background: "#f97316", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, flexShrink: 0 }}>🏈</div>}
                  <div style={{ maxWidth: "75%", background: m.role === "user" ? "#1a2235" : "#0d1320", border: `1px solid ${m.role === "user" ? "#f97316" : "#1a2235"}`, padding: "12px 16px", fontSize: 13, lineHeight: 1.6 }}>
                    {renderMessage(m.content)}
                  </div>
                </div>
              ))}
              {loading && (
                <div style={{ display: "flex", gap: 12 }}>
                  <div style={{ width: 28, height: 28, background: "#f97316", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12 }}>🏈</div>
                  <div style={{ background: "#0d1320", border: "1px solid #1a2235", padding: "12px 16px", fontSize: 13, color: "#64748b" }}>Analyzing...</div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
            <div style={{ padding: "8px 24px", display: "flex", gap: 8, flexWrap: "wrap", borderTop: "1px solid #1a2235" }}>
              {["Best draft strategies for pick #5", "Who should I start at FLEX?", "Is Kelce worth drafting early?", "Best handcuffs to roster", "WR sleepers for 2025"].map(q => (
                <button key={q} className="quick-btn" onClick={() => setInput(q)}>{q}</button>
              ))}
            </div>
            <div style={{ padding: "12px 24px", borderTop: "1px solid #1a2235", display: "flex" }}>
              <input className="input-field" value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === "Enter" && sendMessage()} placeholder="Ask about drafts, lineups, trades, injuries, waiver wire..." />
              <button className="send-btn" onClick={sendMessage} disabled={loading || !input.trim()}>Send →</button>
            </div>
          </div>
        )}

        {activeTab === "draft" && (
          <div style={{ flex: 1, overflowY: "auto", padding: 24, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
            <div>
              <div className="card" style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 10, color: "#f97316", letterSpacing: 2, textTransform: "uppercase", marginBottom: 14 }}>Draft Settings</div>
                <div style={{ fontSize: 10, color: "#64748b", marginBottom: 8, letterSpacing: 1 }}>MY DRAFT POSITION (1-12)</div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(n => (
                    <button key={n} className="draft-pick-btn" style={{ background: draftPick === n ? "#f97316" : undefined, color: draftPick === n ? "#000" : undefined }} onClick={() => setDraftPick(n)}>{n}</button>
                  ))}
                </div>
                <button className="send-btn" style={{ width: "100%" }} onClick={getDraftRec} disabled={draftLoading}>
                  {draftLoading ? "Analyzing..." : `→ Get Pick #${draftPick} Recommendation`}
                </button>
              </div>
              {draftRec && (
                <div className="card" style={{ borderColor: "#f97316" }}>
                  <div style={{ fontSize: 10, color: "#f97316", letterSpacing: 2, textTransform: "uppercase", marginBottom: 12 }}>AI Recommendation</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                    <div style={{ background: posColor(draftRec.position), color: "#000", padding: "2px 8px", fontSize: 10, fontWeight: 700 }}>{draftRec.position}</div>
                    <div style={{ fontSize: 18, fontFamily: "'Bebas Neue',sans-serif" }}>{draftRec.recommendation}</div>
                    <div style={{ marginLeft: "auto", fontSize: 10, padding: "2px 8px", border: `1px solid ${draftRec.risk === "low" ? "#22c55e" : draftRec.risk === "high" ? "#ef4444" : "#f59e0b"}`, color: draftRec.risk === "low" ? "#22c55e" : draftRec.risk === "high" ? "#ef4444" : "#f59e0b" }}>{draftRec.risk?.toUpperCase()} RISK</div>
                  </div>
                  <div style={{ fontSize: 12, color: "#94a3b8", lineHeight: 1.6, marginBottom: 12 }}>{draftRec.reasoning}</div>
                  {draftRec.alternates?.map((a, i) => (
                    <div key={i} style={{ background: "#111827", padding: "8px 12px", marginBottom: 6, fontSize: 12 }}>
                      <span style={{ color: "#f97316" }}>{a.name}</span><span style={{ color: "#64748b", marginLeft: 8 }}>{a.reasoning}</span>
                    </div>
                  ))}
                  <button className="send-btn" style={{ width: "100%", marginTop: 10 }} onClick={() => { setDraftedTeam(prev => [...prev, { name: draftRec.recommendation, pos: draftRec.position }]); setDraftRec(null); }}>
                    ✓ Draft {draftRec.recommendation.replace("Draft ", "")}
                  </button>
                </div>
              )}
            </div>
            <div>
              <div className="card">
                <div style={{ fontSize: 10, color: "#f97316", letterSpacing: 2, textTransform: "uppercase", marginBottom: 14 }}>Top Available</div>
                {DRAFT_POOL.filter(p => !draftedTeam.find(d => d.name === p.name)).slice(0, 10).map(p => (
                  <div key={p.name} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid #1a2235" }}>
                    <div style={{ fontSize: 10, color: "#64748b", width: 20 }}>{p.rank}</div>
                    <div style={{ background: posColor(p.pos), color: "#000", padding: "1px 6px", fontSize: 9, fontWeight: 700, width: 28, textAlign: "center" }}>{p.pos}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13 }}>{p.name}</div>
                      <div style={{ fontSize: 10, color: "#64748b" }}>{p.team} · ADP {p.adp}</div>
                    </div>
                    <div style={{ fontSize: 11, color: "#94a3b8" }}>{p.proj} pts</div>
                  </div>
                ))}
              </div>
              {draftedTeam.length > 0 && (
                <div className="card" style={{ marginTop: 16 }}>
                  <div style={{ fontSize: 10, color: "#f97316", letterSpacing: 2, textTransform: "uppercase", marginBottom: 12 }}>My Team ({draftedTeam.length})</div>
                  {draftedTeam.map((p, i) => (
                    <div key={i} style={{ display: "flex", gap: 10, padding: "6px 0", borderBottom: "1px solid #1a2235", alignItems: "center" }}>
                      <div style={{ fontSize: 10, color: "#64748b" }}>R{i + 1}</div>
                      <div style={{ background: posColor(p.pos), color: "#000", padding: "1px 6px", fontSize: 9, fontWeight: 700 }}>{p.pos}</div>
                      <div style={{ fontSize: 13 }}>{p.name}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === "lineup" && (
          <div style={{ flex: 1, overflowY: "auto", padding: 24 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1.2fr 0.8fr", gap: 20 }}>
              <div>
                <div style={{ fontSize: 10, color: "#f97316", letterSpacing: 2, textTransform: "uppercase", marginBottom: 14 }}>Starting Lineup</div>
                {MOCK_ROSTER.filter(p => !p.slot.startsWith("BE")).map(p => (
                  <div key={p.slot} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", marginBottom: 4, background: "#0d1320", border: `1px solid ${p.status === "out" ? "#ef444430" : "#1a2235"}` }}>
                    <div style={{ fontSize: 9, color: "#64748b", width: 32, textAlign: "center" }}>{p.slot}</div>
                    <div style={{ background: posColor(p.pos), color: "#000", padding: "1px 6px", fontSize: 9, fontWeight: 700, width: 30, textAlign: "center" }}>{p.pos}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, display: "flex", gap: 8, alignItems: "center" }}>
                        {p.name}
                        {p.status !== "healthy" && <span style={{ fontSize: 9, padding: "1px 5px", background: statusColor(p.status) + "20", color: statusColor(p.status) }}>{statusLabel(p.status)}</span>}
                      </div>
                      <div style={{ fontSize: 10, color: "#64748b" }}>{p.team}</div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 13 }}>{p.proj} <span style={{ fontSize: 9, color: "#64748b" }}>proj</span></div>
                      <div style={{ fontSize: 11, color: trendColor(p.trend) }}>{trendIcon(p.trend)} {p.pts}</div>
                    </div>
                  </div>
                ))}
                <div style={{ fontSize: 10, color: "#64748b", letterSpacing: 2, textTransform: "uppercase", margin: "20px 0 10px" }}>Bench</div>
                {MOCK_ROSTER.filter(p => p.slot.startsWith("BE")).map(p => (
                  <div key={p.slot} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 14px", marginBottom: 3, background: "#080c14", border: "1px solid #111827" }}>
                    <div style={{ fontSize: 9, color: "#374151", width: 32, textAlign: "center" }}>{p.slot}</div>
                    <div style={{ background: posColor(p.pos) + "40", color: posColor(p.pos), padding: "1px 6px", fontSize: 9, fontWeight: 700, width: 30, textAlign: "center" }}>{p.pos}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, color: "#94a3b8" }}>{p.name}</div>
                      <div style={{ fontSize: 10, color: "#374151" }}>{p.team}</div>
                    </div>
                    <div style={{ fontSize: 11, color: p.proj === 0 ? "#ef4444" : trendColor(p.trend) }}>{p.proj === 0 ? "OUT" : `${p.proj} proj`}</div>
                  </div>
                ))}
              </div>
              <div>
                <div className="card" style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 10, color: "#f97316", letterSpacing: 2, textTransform: "uppercase", marginBottom: 12 }}>Lineup Alerts</div>
                  <div style={{ background: "#ef444410", border: "1px solid #ef444430", padding: "10px 12px", marginBottom: 8 }}>
                    <div style={{ fontSize: 11, color: "#ef4444", marginBottom: 4 }}>⚠ ACTION NEEDED</div>
                    <div style={{ fontSize: 12 }}>Jaylen Warren is OUT. Replace BE4 immediately.</div>
                    <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 4 }}>→ Suggest: Jerome Ford (CLE)</div>
                  </div>
                  <div style={{ background: "#f59e0b10", border: "1px solid #f59e0b30", padding: "10px 12px", marginBottom: 8 }}>
                    <div style={{ fontSize: 11, color: "#f59e0b", marginBottom: 4 }}>⚡ MONITOR</div>
                    <div style={{ fontSize: 12 }}>Bijan Robinson (Q) — ankle. Watch Friday.</div>
                  </div>
                  <div style={{ background: "#22c55e10", border: "1px solid #22c55e30", padding: "10px 12px" }}>
                    <div style={{ fontSize: 11, color: "#22c55e", marginBottom: 4 }}>✓ CONFIRMED</div>
                    <div style={{ fontSize: 12 }}>McCaffrey — full practice, no designation.</div>
                  </div>
                </div>
                <div className="card">
                  <div style={{ fontSize: 10, color: "#f97316", letterSpacing: 2, textTransform: "uppercase", marginBottom: 12 }}>Matchup Grades</div>
                  {[
                    { name: "L. Jackson", opp: "@ CLE", grade: "A+", note: "CLE 31st pass D" },
                    { name: "CMC", opp: "vs JAX", grade: "A+", note: "JAX 30th run D" },
                    { name: "T. Hill", opp: "vs NE", grade: "B", note: "Shadow CB concern" },
                    { name: "Amon-Ra", opp: "@ LV", grade: "A", note: "LV 28th pass D" },
                    { name: "SF DST", opp: "vs JAX", grade: "A", note: "JAX 4.8 sacks/g allowed" },
                  ].map(m => (
                    <div key={m.name} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 0", borderBottom: "1px solid #111827" }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 12 }}>{m.name}</div>
                        <div style={{ fontSize: 10, color: "#64748b" }}>{m.note}</div>
                      </div>
                      <div style={{ fontSize: 10, color: "#64748b" }}>{m.opp}</div>
                      <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 20, color: m.grade.startsWith("A") ? "#22c55e" : "#f59e0b" }}>{m.grade}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "trade" && (
          <div style={{ flex: 1, overflowY: "auto", padding: 24, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
            <div>
              <div className="card">
                <div style={{ fontSize: 10, color: "#f97316", letterSpacing: 2, textTransform: "uppercase", marginBottom: 16 }}>Trade Analyzer</div>
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 10, color: "#ef4444", letterSpacing: 1, marginBottom: 8 }}>I AM GIVING</div>
                  <textarea className="input-field" rows={3} value={tradeGive} onChange={e => setTradeGive(e.target.value)} placeholder="e.g. Davante Adams, Tony Pollard" style={{ resize: "none" }} />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 10, color: "#22c55e", letterSpacing: 1, marginBottom: 8 }}>I AM GETTING</div>
                  <textarea className="input-field" rows={3} value={tradeGet} onChange={e => setTradeGet(e.target.value)} placeholder="e.g. Travis Kelce" style={{ resize: "none" }} />
                </div>
                <button className="send-btn" style={{ width: "100%" }} onClick={analyzeTrade} disabled={tradeLoading || !tradeGive.trim() || !tradeGet.trim()}>
                  {tradeLoading ? "Analyzing..." : "→ Analyze This Trade"}
                </button>
              </div>
              <div className="card" style={{ marginTop: 16 }}>
                <div style={{ fontSize: 10, color: "#64748b", letterSpacing: 2, textTransform: "uppercase", marginBottom: 12 }}>Quick Examples</div>
                {[
                  { give: "Davante Adams, Tony Pollard", get: "Travis Kelce" },
                  { give: "Stefon Diggs", get: "Breece Hall" },
                  { give: "Austin Ekeler, Puka Nacua", get: "Justin Jefferson" },
                ].map((ex, i) => (
                  <button key={i} className="quick-btn" style={{ width: "100%", marginBottom: 6, display: "block" }} onClick={() => { setTradeGive(ex.give); setTradeGet(ex.get); }}>
                    <span style={{ color: "#ef4444" }}>GIVE</span> {ex.give} → <span style={{ color: "#22c55e" }}>GET</span> {ex.get}
                  </button>
                ))}
              </div>
            </div>
            <div>
              {tradeResult ? (
                <div className="card" style={{ borderColor: tradeResult.verdict === "ACCEPT" ? "#22c55e" : tradeResult.verdict === "DECLINE" ? "#ef4444" : "#f59e0b" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                    <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 32, color: tradeResult.verdict === "ACCEPT" ? "#22c55e" : tradeResult.verdict === "DECLINE" ? "#ef4444" : "#f59e0b" }}>{tradeResult.verdict}</div>
                    {tradeResult.score && (
                      <div style={{ marginLeft: "auto", textAlign: "center" }}>
                        <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 36, color: "#f97316" }}>{tradeResult.score}<span style={{ fontSize: 16 }}>/10</span></div>
                        <div style={{ fontSize: 9, color: "#64748b", letterSpacing: 1 }}>YOUR VALUE</div>
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: "#94a3b8", lineHeight: 1.7, marginBottom: 14 }}>{tradeResult.analysis}</div>
                  {tradeResult.pros?.length > 0 && (
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 10, color: "#22c55e", letterSpacing: 1, marginBottom: 6 }}>PROS</div>
                      {tradeResult.pros.map((p, i) => <div key={i} style={{ fontSize: 12, padding: "4px 0", borderBottom: "1px solid #111827" }}>✓ {p}</div>)}
                    </div>
                  )}
                  {tradeResult.cons?.length > 0 && (
                    <div style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: 10, color: "#ef4444", letterSpacing: 1, marginBottom: 6 }}>CONS</div>
                      {tradeResult.cons.map((c, i) => <div key={i} style={{ fontSize: 12, padding: "4px 0", borderBottom: "1px solid #111827" }}>✗ {c}</div>)}
                    </div>
                  )}
                  {tradeResult.counter && (
                    <div style={{ background: "#f59e0b10", border: "1px solid #f59e0b30", padding: "10px 12px" }}>
                      <div style={{ fontSize: 10, color: "#f59e0b", letterSpacing: 1, marginBottom: 6 }}>COUNTER OFFER</div>
                      <div style={{ fontSize: 12 }}>{tradeResult.counter}</div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="card" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 200, color: "#374151", textAlign: "center" }}>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>🔄</div>
                  <div style={{ fontSize: 12, letterSpacing: 1 }}>Enter a trade to get AI analysis</div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === "waiver" && (
          <div style={{ flex: 1, overflowY: "auto", padding: 24 }}>
            <div style={{ fontSize: 10, color: "#f97316", letterSpacing: 2, textTransform: "uppercase", marginBottom: 16 }}>Waiver Wire Priority</div>
            {MOCK_WAIVERS.map((w, i) => (
              <div key={w.name} className="card" style={{ marginBottom: 8, display: "flex", gap: 14, alignItems: "center" }}>
                <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 28, color: i === 0 ? "#f97316" : "#374151", width: 32, textAlign: "center" }}>{i + 1}</div>
                <div style={{ background: posColor(w.pos), color: "#000", padding: "2px 8px", fontSize: 9, fontWeight: 700 }}>{w.pos}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, marginBottom: 2 }}>{w.name} <span style={{ fontSize: 10, color: "#64748b" }}>{w.team}</span></div>
                  <div style={{ fontSize: 11, color: "#94a3b8" }}>{w.reason}</div>
                </div>
                <div style={{ textAlign: "right", minWidth: 80 }}>
                  <div style={{ fontSize: 13 }}>{w.proj} <span style={{ fontSize: 9, color: "#64748b" }}>proj</span></div>
                  <div style={{ fontSize: 10, color: "#64748b" }}>{w.owned}% owned</div>
                </div>
                <div style={{ padding: "4px 10px", fontSize: 10, letterSpacing: 1, fontWeight: 700, border: `1px solid ${w.add === "YES" ? "#22c55e" : "#f59e0b"}`, color: w.add === "YES" ? "#22c55e" : "#f59e0b" }}>{w.add}</div>
              </div>
            ))}
          </div>
        )}

        {activeTab === "news" && (
          <div style={{ flex: 1, overflowY: "auto", padding: 24 }}>
            <div style={{ fontSize: 10, color: "#f97316", letterSpacing: 2, textTransform: "uppercase", marginBottom: 16 }}>Intel Feed — Updated Daily</div>
            {MOCK_NEWS.map((n, i) => {
              const typeColors = { injury: "#ef4444", move: "#f59e0b", presser: "#3b82f6", camp: "#22c55e", matchup: "#a855f7", waiver: "#06b6d4" };
              return (
                <div key={i} className="card" style={{ marginBottom: 8, borderLeft: `3px solid ${typeColors[n.type]}` }}>
                  <div style={{ display: "flex", gap: 10, marginBottom: 8, alignItems: "center" }}>
                    <div style={{ background: typeColors[n.type] + "20", color: typeColors[n.type], padding: "2px 8px", fontSize: 9, letterSpacing: 1, textTransform: "uppercase" }}>{n.type}</div>
                    <div style={{ fontSize: 10, color: "#64748b" }}>{n.player} · {n.team}</div>
                    <div style={{ marginLeft: "auto", fontSize: 10, color: "#374151" }}>{n.time}</div>
                    {n.severity === "high" && <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#ef4444", boxShadow: "0 0 6px #ef4444" }} />}
                  </div>
                  <div style={{ fontSize: 14, marginBottom: 6 }}>{n.headline}</div>
                  <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.6 }}>{n.detail}</div>
                </div>
              );
            })}
          </div>
        )}

      </div>
    </div>
  );
}